import React, { memo, useCallback, useEffect, useState } from 'react'
import { Form, Skeleton, Spin } from 'antd'
import { useSelector } from 'react-redux'
import {
  getAppealsFormData,
  getAppealsFormError,
  getAppealsFormLoading,
  getVisibleFormData,
} from '../../selectors'
import { useDebounce } from '../../../../../hooks/useDebounce/useDebounce'
import { HStack, VStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { useNavigate } from 'react-router-dom'
import { isFormDataRequired } from 'src/lib/utils/collections'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'
import { setAppealsFormData } from '../../../../../store/appeal/actions'

interface AppealsFormsProps {
  className?: string
  id: string | number
  isFormProcessing?: boolean
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const AppealsForms: React.FC<AppealsFormsProps> = memo((props: AppealsFormsProps) => {
  const { id, isFormProcessing = false } = props
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const appealsFormData = useSelector(getAppealsFormData)
  const appealsFormIsLoading = useSelector(getAppealsFormLoading)
  const appealsFormError = useSelector(getAppealsFormError)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()
  const [isFormDisabled, setIsFormDisabled] = useState(false)
  const isNewAppeal = id === 'new'
  const isFormBlocked = isFormDisabled || isFormProcessing

  const saveCase = useCallback(
    async (nextCardType?: string) => {
      return saveResult(id, visibleFormData, appealsFormData?.stages?.current, nextCardType)
    },
    [appealsFormData?.stages?.current, id, saveResult, visibleFormData],
  )

  useEffect(() => {
    const redirectUrl = appealsFormData?.redirectUrl
    if (redirectUrl) {
      dispatch(setAppealsFormData(null as any))
      navigate(redirectUrl)
    }
  }, [appealsFormData, dispatch, navigate])

  const handleAutoSave = useCallback(async () => {
    const shouldLockForm = isNewAppeal
    if (shouldLockForm) {
      setIsFormDisabled(true)
    }
    try {
      return await saveCase()
    } finally {
      if (shouldLockForm) {
        setIsFormDisabled(false)
      }
    }
  }, [isNewAppeal, saveCase])

  const debounceSaveCase = useDebounce(handleAutoSave, 1000)

  const canAutoSave = useCallback(
    (formData: any) => {
      if (!visibleFormData) {
        return false
      }
      if (!isNewAppeal) {
        return true
      }
      return isFormDataRequired(formData, visibleFormData.resultSchema)
    },
    [isNewAppeal, visibleFormData],
  )

  const onChangeForm = useCallback(
    async ({ formData }) => {
      if (!visibleFormData || isFormBlocked) {
        return
      }
      const isFilesEdit = JSON.stringify(formData) === JSON.stringify(visibleFormData?.resultObject)
      if (isFilesEdit) {
        return
      }
      const nextFormData = { ...formData }
      visibleFormData.resultObject = nextFormData

      if (!canAutoSave(nextFormData)) {
        return
      }

      debounceSaveCase()
    },
    [visibleFormData, debounceSaveCase, canAutoSave, isFormBlocked],
  )

  if (appealsFormError && !appealsFormIsLoading && !appealsFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка. Попробуйте перезагрузить страницу или открыть
        карточку повторно.
      </HStack>
    )
  }

  return (
    <>
      <HStack gap={'8'} max>
        <Form
          {...formItemLayout}
          layout="horizontal"
          form={form}
          scrollToFirstError={true}
          style={{ width: '100%' }}
          disabled={isFormBlocked}
        >
          {appealsFormIsLoading && !visibleFormData ? (
            <Skeleton active />
          ) : (
            <VStack
              gap={'16'}
              max
              align={'stretch'}
              className={'appealsForm'}
              data-tour-id="appeal-form"
            >
              <Spin spinning={isFormBlocked}>
                {visibleFormData && (
                  <DefaultJsonForm
                    formData={{ ...visibleFormData.resultObject }}
                    schema={visibleFormData.resultSchema}
                    onChange={onChangeForm}
                    orientation="horizontal"
                  />
                )}
              </Spin>
            </VStack>
          )}
        </Form>
      </HStack>
    </>
  )
})

AppealsForms.displayName = 'AppealsForms'
